// schemas
import { parseISO } from "date-fns";

import { DateQuickFilterValue, FieldFilter, FilterComparison, SelectedFilter } from "../../../schemas/report";

import FiltersConfig from "../Components/Filters/Config";

/**
 * handles translating the date filters to advanced filters.
 */
export const quickDateFilterToAdvancedDateFilter = (
	startDate: Date,
	endDate: Date,
	dateField:
		| "startDate"
		| "completedDate"
		| "hiredBetweenDate"
		| "goalCreatedBetweenDate"
		| "goalStartBetweenDate"
		| "goalDueBetweenDate"
		| "surveyStartedBetween"
		| "surveyCompletedBetween",
	category: "REVIEW_FIELD" | "EMPLOYEE_FIELD" | "GOAL_FIELD" | "SURVEY_FIELD"
) => {
	// TODO - the field shouldn't require a label to filter the data
	const field = {
		startDate: "start_date",
		completedDate: "completed_date",
		hiredBetweenDate: "Hire Date",
		goalCreatedBetweenDate: "creation_date",
		goalStartBetweenDate: "start_date",
		goalDueBetweenDate: "end_date",
		surveyStartedBetween: "survey_start_date",
		surveyCompletedBetween: "survey_end_date",
	};

	return [
		[
			{
				comparison: FilterComparison.GreaterThanOrEqual,
				type: "date",
				field: field[dateField],
				category,
				value: startDate,
			} satisfies FieldFilter,
		],
		[
			{
				comparison: FilterComparison.LessThanOrEqual,
				type: "date",
				field: field[dateField],
				category,
				value: endDate,
			} satisfies FieldFilter,
		],
	] satisfies FieldFilter[][];
};

/**
 * Translates the quick filter into an advanced filter that can be sent to
 * the server.
 */
export const translateQuickFilterToAdvancedFilter = (quickFilters: SelectedFilter[]): FieldFilter[][] => {
	const allFilters: FieldFilter[][] = [];
	quickFilters.forEach((quickFilter) => {
		const filterValue = quickFilter.value?.value || quickFilter.value;
		/**
		 * Employee Fields
		 */

		if (quickFilter.type === "asOf") {
			const formattedValue = {
				comparison: FilterComparison.LessThanOrEqual,
				type: "date",
				value: filterValue,
				field: "as_of",
				category: "EMPLOYEE_FIELD",
			} satisfies FieldFilter;
			allFilters.push([formattedValue]);
		}

		if (quickFilter.type === "employeeStatus") {
			const formattedValues = filterValue.map((val: string) => {
				return {
					comparison: FilterComparison.Equals,
					type: "employeeStatus",
					value: val,
					field: "Activation Status",
					category: "EMPLOYEE_FIELD",
				} satisfies FieldFilter;
			});
			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}
		if (quickFilter.type === "department") {
			const groupFilterValue = filterValue;

			const formattedValues = groupFilterValue.map((val: { value: string; label: string }) => {
				return {
					comparison: FilterComparison.Equals,
					type: "department",
					value: val.value,
					field: "Group",
					category: "EMPLOYEE_FIELD",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}

		/**
		 * Review fields
		 */

		if (quickFilter.type === "reviewStatus") {
			const reviewStatusFilterValue = filterValue;
			const formattedValues = reviewStatusFilterValue.map((val: string) => {
				return {
					comparison: FilterComparison.Equals,
					type: "text",
					value: val,
					field: "status",
					category: "REVIEW_FIELD",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}

		if (quickFilter.type === "reviewReportStatus") {
			const reviewReportStatusValue = filterValue;
			const formattedValues = reviewReportStatusValue.map((val: string) => {
				return {
					comparison: FilterComparison.Equals,
					type: "reviewReportStatus",
					value: val,
					field: "status",
					category: "REVIEW_REPORT_FIELD",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}

		if (quickFilter.type === "reviewCycleNames") {
			const reviewCycleFilter = filterValue;

			const formattedValues = reviewCycleFilter.map((val: { value: string; label: string }) => {
				return {
					value: val.value,
					comparison: FilterComparison.Equals,
					field: "review_cycle",
					category: "REVIEW_CYCLE_FIELD",
					type: "cycle",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}

		if (quickFilter.type === "reviewFormNames") {
			const reviewFormFilter = filterValue;
			const formattedValues = reviewFormFilter.map((val: { value: string; label: string }) => {
				return {
					value: val.value,
					comparison: FilterComparison.Equals,
					field: "name",
					category: "REVIEW_FORM_FIELD",
					type: "text",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}

		if (quickFilter.type === "subjectId") {
			const subjectIdFilterValues = filterValue;
			const formattedValues = subjectIdFilterValues.map((val: { value: string; label: string }) => {
				return {
					value: val.value,
					comparison: FilterComparison.Equals,
					field: "subject",
					category: "SUBJECT",
					type: "subject",
				} satisfies FieldFilter;
			});
			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}

		/**
		 * Goal Fields
		 */

		if (["goalStatus", "goalTransparency"].includes(quickFilter.type)) {
			const goalAssigneeFilterValues = filterValue;
			const formattedValues = goalAssigneeFilterValues.map((val: string) => {
				return {
					value: val,
					comparison: FilterComparison.Equals,
					field: FiltersConfig.goalFieldsMap[quickFilter.type],
					category: "GOAL_FIELD",
					type: quickFilter.type,
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}
		if (quickFilter.type === "goalOutlook") {
			const goalOutlookFilterValues = filterValue;
			const formattedValues = goalOutlookFilterValues.map((val: { id: string; color: string; name: string }) => {
				return {
					value: val.id,
					comparison: FilterComparison.Equals,
					field: FiltersConfig.goalFieldsMap[quickFilter.type],
					category: "GOAL_FIELD",
					type: "goalOutlook",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}
		if (quickFilter.type === "goalAssignee") {
			const goalAssigneeFilterValues = filterValue;
			const formattedValues = goalAssigneeFilterValues.map((val: { value: string; label: string }) => {
				return {
					value: val.value,
					comparison: FilterComparison.Equals,
					field: FiltersConfig.goalFieldsMap[quickFilter.type],
					category: "GOAL_FIELD",
					type: "person",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}
		if (quickFilter.type === "goalCategories") {
			const goalCategoryFilterValues = filterValue;
			const formattedValues = goalCategoryFilterValues.map((val: { value: string; label: string }) => {
				return {
					value: val.value,
					comparison: FilterComparison.Equals,
					field: FiltersConfig.goalFieldsMap[quickFilter.type],
					category: "GOAL_FIELD",
					type: "goalCategories",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}
		if (quickFilter.type === "surveyName") {
			const surveyNames = filterValue;
			const formattedValues = surveyNames.map((val: { value: string; label: string }) => {
				return {
					value: val.value,
					comparison: FilterComparison.Equals,
					field: "survey_id",
					category: "SURVEY_FIELD",
					type: "surveyName",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}

		if (quickFilter.type === "surveyStatus") {
			const surveyStatusValue = filterValue;
			const formattedValues = surveyStatusValue.map((val: string) => {
				return {
					comparison: FilterComparison.Equals,
					type: "suveyStatus",
					value: val,
					field: "survey_status",
					category: "SURVEY_FIELD",
				} satisfies FieldFilter;
			});

			if (formattedValues.length > 0) {
				allFilters.push(formattedValues);
			}
		}

		if (quickFilter.type === "surveyResponseStatus") {
			// Radio button returns { value, label } - skip if "all" selected
			const selectedValue = filterValue?.value || filterValue;
			if (selectedValue && selectedValue !== "all") {
				const formattedValue = {
					comparison: FilterComparison.Equals,
					type: "responseStatus",
					value: selectedValue,
					field: "survey_response_id",
					category: "SURVEY_FIELD",
				} satisfies FieldFilter;
				allFilters.push([formattedValue]);
			}
		}

		/**
		 * Date fields:
		 */

		if (FiltersConfig.dateFields.includes(quickFilter.type)) {
			const filterValue = quickFilter.value;

			// confirm quick filter value start and end are of date types
			if (!(filterValue.start instanceof Date) || !(filterValue.end instanceof Date)) {
				// throw new Error('Quick filter value start and end must be of date types');
				return [];
			}

			if (quickFilter.type === "startedBetween") {
				const startedBetweenFilters = quickDateFilterToAdvancedDateFilter(
					filterValue.start,
					filterValue.end,
					"startDate",
					"REVIEW_FIELD"
				);
				allFilters.push(...startedBetweenFilters);
			} else if (quickFilter.type === "completedBetween") {
				const completedBetweenFilters = quickDateFilterToAdvancedDateFilter(
					filterValue.start,
					filterValue.end,
					"completedDate",
					"REVIEW_FIELD"
				);
				allFilters.push(...completedBetweenFilters);
			} else if (quickFilter.type === "hiredBetween") {
				const hiredBetweenFilters = quickDateFilterToAdvancedDateFilter(
					filterValue.start,
					filterValue.end,
					"hiredBetweenDate",
					"EMPLOYEE_FIELD"
				);
				allFilters.push(...hiredBetweenFilters);
			} else if (
				["goalCreatedBetweenDate", "goalStartBetweenDate", "goalDueBetweenDate"].includes(quickFilter.type)
			) {
				const goalDateFilters = quickDateFilterToAdvancedDateFilter(
					filterValue.start,
					filterValue.end,
					quickFilter.type as "goalCreatedBetweenDate" | "goalStartBetweenDate" | "goalDueBetweenDate",
					"GOAL_FIELD"
				);
				allFilters.push(...goalDateFilters);
			} else if (["surveyStartedBetween", "surveyCompletedBetween"].includes(quickFilter.type)) {
				const surveyDateFilters = quickDateFilterToAdvancedDateFilter(
					filterValue.start,
					filterValue.end,
					quickFilter.type as "surveyStartedBetween" | "surveyCompletedBetween",
					"SURVEY_FIELD"
				);
				allFilters.push(...surveyDateFilters);
			}
		}
	});

	return allFilters;
};

/**
 * Formats the quickFilter given from the server so that if the filter is of date type, the date ISO strings are converted to Date objects.
 */
export const formatQuickFilter = (quickFilter: SelectedFilter[]): SelectedFilter[] => {
	const formattedQuickFilters = quickFilter
		.map((filter) => {
			if (
				[
					"startedBetween",
					"completedBetween",
					"hiredBetween",
					"goalCreatedBetweenDate",
					"goalStartBetweenDate",
					"goalDueBetweenDate",
					"surveyStartedBetween",
					"surveyCompletedBetween",
				].includes(filter.type)
			) {
				const filterValue = filter.value as DateQuickFilterValue;

				// ignores any existing bad data PY-7719 bug
				// will correct upon next save of report
				if (
					filterValue?.start === null ||
					filterValue?.end === null ||
					typeof filterValue?.start !== ("string" || "date") ||
					typeof filterValue?.end !== ("string" || "date")
				) {
					return null; // Skip this filter
				}
				const start = filterValue.start as string;
				const end = filterValue.end as string;

				return {
					type: filter.type,
					value: {
						start: parseISO(start),
						end: parseISO(end),
					},
				};
			} else {
				return filter;
			}
		})
		.filter((filter): filter is SelectedFilter => filter !== null);

	return formattedQuickFilters;
};
